import { useGraphqlMutation, useGraphqlQuery } from "../query.utils/query.hooks"
import { gql } from "graphql-request"
import { queryClient } from "../../App"
import { useAuth } from "../context/auth"
import { useCallback, useEffect, useState } from "react"

const LOCAL_STORAGE_KEY = "mapspot_tracked_points"

/** Read tracked point IDs from localStorage */
const getLocalTrackedPoints = (): number[] => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

/** Write tracked point IDs to localStorage */
const setLocalTrackedPoints = (pointIds: number[]) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(pointIds))
}

/** Clear local tracked points (after sync to server) */
export const clearLocalTrackedPoints = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEY)
}

/** Check if there are unsaved local tracked points */
export const hasLocalTrackedPoints = (): boolean => {
  return getLocalTrackedPoints().length > 0
}

const useTrackedPoints = () => {
  const { user } = useAuth()
  const [localPoints, setLocalPointsState] = useState<number[]>(getLocalTrackedPoints)

  // Server query — only runs when logged in
  const query = useGraphqlQuery<{ trackedPointIds: number[] }>(
    "trackedPointIds",
    gql`
      query trackedPointIds {
        trackedPointIds
      }
    `,
    "POST",
    undefined,
    { enabled: !!user },
  )

  const setTrackedPointsMutation = useGraphqlMutation<
    number[],
    { pointIds: number[] },
    unknown,
    { previousTrackedPointIds: number[] }
  >(
    "setTrackedPoints",
    gql`
      mutation setTrackedPoints($pointIds: [Int!]!) {
        setTrackedPoints(pointIds: $pointIds)
      }
    `,
    {
      onMutate: async ({ pointIds }) => {
        await queryClient.cancelQueries({ queryKey: ["trackedPointIds"] })
        const previousTrackedPointIds =
          (queryClient.getQueryData(["trackedPointIds"]) as typeof query.data)?.trackedPointIds || []
        queryClient.setQueryData(["trackedPointIds"], {
          trackedPointIds: pointIds,
        })
        return { previousTrackedPointIds }
      },
      onError: (err, updatePayload, context) => {
        console.error("Error removing tracked point", err)
        queryClient.setQueryData(["trackedPointIds"], context?.previousTrackedPointIds || [])
      },
    },
  )

  // Sync localStorage → server on login
  useEffect(() => {
    if (user && query.data) {
      const localPts = getLocalTrackedPoints()
      if (localPts.length > 0) {
        const serverPts = query.data.trackedPointIds || []
        const merged = Array.from(new Set([...serverPts, ...localPts]))
        if (merged.length !== serverPts.length) {
          setTrackedPointsMutation.mutateAsync({ pointIds: merged }).then(() => {
            clearLocalTrackedPoints()
            setLocalPointsState([])
          })
        } else {
          clearLocalTrackedPoints()
          setLocalPointsState([])
        }
      }
    }
  }, [user, query.data?.trackedPointIds])

  const setTrackedPoints = useCallback(
    async ({ pointIds }: { pointIds: number[] }) => {
      if (user) {
        await setTrackedPointsMutation.mutateAsync({ pointIds })
      } else {
        setLocalTrackedPoints(pointIds)
        setLocalPointsState(pointIds)
      }
    },
    [user, setTrackedPointsMutation],
  )

  const trackedPointIds = user ? query.data?.trackedPointIds || [] : localPoints

  return {
    trackedPointIds,
    setTrackedPoints,
  }
}

export default useTrackedPoints
